import type { LocationDescriptor, LocationDescriptorObject } from 'history';
import * as React from 'react';
import { Link } from 'react-router-dom';
import type { ReactRef } from '../../../modules/react-utils';
import cn from '~modules/cn';

import './hybrid-link.scss';

const isNetlifyUrl = (url: string) => {
    const isAnchor = url.charAt?.(0) === '#';
    const isExternal = url && !isAnchor;
    let isNetlifyProperty = !isExternal;

    if (isExternal) {
        try {
            const { origin } = new URL(url);
            isNetlifyProperty = /\.netlify\.com$/.test(origin) || /\.stackbit\.com$/.test(origin);
        } catch (_) {
            // ignore
        }
    }

    return isNetlifyProperty;
};

export interface Props {
    title?: string;
    to?: LocationDescriptor;
    target?: string;
    className?: string;
    children?: React.ReactNode;
    onClick?: (e: React.MouseEvent) => void;
    setRef?: ReactRef<HTMLElement | null>;
    'data-tip'?: string;
    forceLink?: boolean;
    disabled?: boolean;
    Tag?: 'div' | 'span';
    'data-testid'?: string;
    useBaseStyles?: boolean;
    'aria-label'?: string;
}

export default function HybridLink({
    title,
    to,
    target,
    className,
    children,
    onClick,
    disabled,
    setRef,
    'data-tip': tip,
    'data-testid': testId,
    forceLink,
    Tag = 'div',
    useBaseStyles = true,
    'aria-label': ariaLabel
}: Props): JSX.Element {
    const attrs = {
        'data-tip': tip,
        'data-testid': testId,
        className: useBaseStyles ? cn('hybrid-link', className, { 'hybrid-link--disabled': disabled }) : className,
        'aria-label': ariaLabel
    };

    if (to || forceLink) {
        const href = to ? (to as LocationDescriptorObject).pathname || (to as string) : '';
        if (to && href.startsWith('/')) {
            return (
                <Link target={target} ref={setRef as ReactRef<HTMLAnchorElement>} title={title} onClick={onClick} to={to} {...attrs}>
                    {children}
                </Link>
            );
        }
        return (
            <a
                ref={setRef as ReactRef<HTMLAnchorElement>}
                title={title}
                onClick={onClick}
                href={to && href}
                target={target || '_blank'}
                rel={isNetlifyUrl(href) ? 'noopener' : 'noopener noreferrer'}
                {...attrs}
            >
                {children}
            </a>
        );
    }
    return onClick ? (
        <Tag ref={setRef as ReactRef<HTMLDivElement>} title={title} onClick={onClick} {...attrs}>
            {children}
        </Tag>
    ) : (
        <>{children}</>
    );
}
